import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Heart, Calendar, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Cultural Toolkit card — small panel on the appraisal page that shows
 * the client's preferred greeting/thank-you/farewell + upcoming
 * culturally-significant dates. Only renders content when the client's
 * `cultural_identity` is set. When unset it prompts the operator to
 * pick one from the client form.
 */
export default function CulturalToolkitCard({ api, clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/cultural-toolkit/client/${clientId}`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, clientId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!data) return null;

  // Unconfigured — small CTA so the operator knows the feature exists.
  if (!data.configured) {
    return (
      <div
        className="bg-white border border-dashed border-slate-300 rounded-md p-3 text-xs text-slate-500 flex items-center gap-2"
        data-testid="cultural-toolkit-empty"
      >
        <Globe size={14} className="text-slate-400" />
        <span>
          Cultural identity not set for this client.{' '}
          <span className="text-slate-600">Pick one on the Client form to surface greetings + celebration reminders.</span>
        </span>
      </div>
    );
  }

  const { phrase, upcoming_celebrations: upcoming = [] } = data;

  return (
    <div
      className="bg-gradient-to-br from-amber-50/60 via-white to-rose-50/40 border border-amber-200 rounded-md p-3"
      data-testid="cultural-toolkit-card"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="cultural-toolkit-toggle"
      >
        {collapsed ? <ChevronRight size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
        <Globe size={14} className="text-rose-500" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
          Cultural Toolkit · {phrase.label}
        </span>
        <span className="ml-auto text-[10px] text-slate-500 italic">{phrase.language}</span>
      </button>

      {!collapsed && (
        <div className="mt-3 space-y-3">
          {/* Phrases row — three pills */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="cultural-toolkit-phrases">
            <PhrasePill label="Greeting" value={phrase.greeting} testid="cultural-phrase-greeting" />
            <PhrasePill label="Thank you" value={phrase.thank_you} testid="cultural-phrase-thanks" />
            <PhrasePill label="Farewell"  value={phrase.farewell}  testid="cultural-phrase-farewell" />
          </div>

          {/* Extra phrases (if seeded) */}
          {Array.isArray(phrase.extra_phrases) && phrase.extra_phrases.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {phrase.extra_phrases.map((p, i) => (
                <span
                  key={i}
                  className="px-2 py-1 rounded bg-white ring-1 ring-amber-200 text-[11px] text-slate-700"
                  title={p.label}
                >
                  <span className="text-slate-400 mr-1">{p.label}:</span>{p.phrase}
                </span>
              ))}
            </div>
          )}

          {/* Upcoming celebrations */}
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
              <Calendar size={11} /> Upcoming celebrations
            </div>
            {upcoming.length === 0 && (
              <p className="text-xs text-slate-400 italic">No upcoming dates configured for this culture yet.</p>
            )}
            <div className="space-y-1.5" data-testid="cultural-toolkit-upcoming">
              {upcoming.map((u) => (
                <CelebrationRow key={u._id} item={u} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PhrasePill({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="bg-white rounded-md ring-1 ring-amber-200 px-2.5 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
      <div className="text-sm font-bold text-slate-800 flex items-start gap-1.5">
        <Heart size={11} className="text-rose-400 mt-1 flex-shrink-0" />
        <span className="break-words">{value}</span>
      </div>
    </div>
  );
}

function CelebrationRow({ item }) {
  const d = item.days_until;
  const colour =
    d <= item.reminder_days_before ? 'bg-rose-50 ring-rose-200 text-rose-800' :
    d <= 30                         ? 'bg-amber-50 ring-amber-200 text-amber-800' :
                                      'bg-slate-50 ring-slate-200 text-slate-700';
  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded ring-1 text-xs ${colour}`}
      data-testid={`cultural-celebration-row-${item._id}`}
    >
      <Calendar size={12} className="flex-shrink-0 opacity-70" />
      <span className="font-bold flex-shrink-0">{item.name}</span>
      <span className="text-[11px] opacity-70">·</span>
      <span className="font-mono text-[11px]">{item.next_date}</span>
      <span className="ml-auto text-[11px] font-semibold">
        {d === 0 ? 'TODAY' : d === 1 ? 'tomorrow' : `${d} day${d === 1 ? '' : 's'}`}
      </span>
    </div>
  );
}
